// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  readTooltipsEnabled,
  TOOLTIPS_CHANGED_EVENT,
} from "@/lib/tooltipPreferences";

/** Returns whether in-app help tooltips should be shown (defaults to true). */
export function useTooltipsEnabled(): boolean {
  const { profile } = useAuth();
  const userId = profile?.id;
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!userId) {
      setEnabled(true);
      return;
    }

    setEnabled(readTooltipsEnabled(userId));

    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ userId: string; enabled: boolean }>).detail;
      if (detail?.userId === userId) {
        setEnabled(detail.enabled);
      }
    };

    window.addEventListener(TOOLTIPS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(TOOLTIPS_CHANGED_EVENT, onChange);
  }, [userId]);

  return enabled;
}
